import type { ConvexUser } from '@packages/shared';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { Authenticated, AuthLoading, Unauthenticated } from 'convex/react';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import { SignIn } from '@/auth/SignIn';
import type { BreadcrumbData } from '@/components/ui+/breadcrumbs';
import type { NavLink } from '@/utils/navigation';

// Other things could be added to the router context, but we only need the convex user for now.
export type AppRouterContext = {
  convexUser: ConvexUser;
  /** Is the user authenticated on the client AND the server */
  isAuthenticated: boolean;
  /** Nav links that may be attached to the current layout and made available to it's child routes */
  // Wait until https://github.com/TanStack/router/issues/1635 is fixed before using this.
  navLinks?: NavLink[];
};

// Augment TanStack Router's StaticDataRouteOption to include breadcrumbs
// https://tanstack.com/router/latest/docs/framework/react/guide/static-route-data#enforcing-static-data
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    breadcrumbs?: BreadcrumbData[];
    /** Render this route flush to the content area — no padding on the layout's `<main>`. */
    noPagePadding?: boolean;
  }
}

// Note use of `createRootRouteWithContext({ ..})`, rather than `createRootRoute({ ..})`
export const Route = createRootRouteWithContext<AppRouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  return (
    <>
      <AuthLoading>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="text-gray-400 h-6 w-6 animate-spin" />
        </div>
      </AuthLoading>
      <Unauthenticated>
        <SignIn />
      </Unauthenticated>
      <Authenticated>
        <Outlet />
      </Authenticated>
      {/* https://sonner.emilkowal.ski/toaster#api-reference */}
      <Toaster richColors={true} duration={6000} />
      {/*<TanStackRouterDevtools position="bottom-right" />*/}
    </>
  );
}
